import * as React from 'react'

import {
  Field,
  SingleDatePicker,
  Space,
  Text,
  TextField,
  TextLabel,
} from '@procurify/ui'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ReconcileSession } from '@webapp/modules/accounts-payable/entities/ReconcileSession'
import messages from './messages'

export type SessionStatementControlsStatus =
  | 'idle'
  | 'pending'
  | 'resolved'
  | 'rejected'

export interface ISessionStatementControlsProps {
  session: ReconcileSession
  /** Card currency label beside the balance input */
  currencyName: string
  /** "Last statement: {date}" caption source */
  lastStatementDate: Nullable<Date>
  statementDateValue: Nullable<Date>
  isDatePickerFocused: boolean
  onDatePickerFocusChange: (focused: boolean) => void
  onStatementDateChange: (date: Date) => void
  balanceValue: string
  /** Persist status — input disabled while 'pending' (Inline-Edit iron law: View owns NO state) */
  status: SessionStatementControlsStatus
  onBalanceChange: (value: string) => void
  onBalanceBlur: () => void
  onBalanceKeyDown: (event: React.KeyboardEvent) => void
}

const isAfterToday = (day: Date) => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return day.getTime() > today.getTime()
}

export const SessionStatementControls = ({
  currencyName,
  lastStatementDate,
  statementDateValue,
  isDatePickerFocused,
  onDatePickerFocusChange,
  onStatementDateChange,
  balanceValue,
  status,
  onBalanceChange,
  onBalanceBlur,
  onBalanceKeyDown,
}: ISessionStatementControlsProps) => {
  const intl = useProcurifyIntl()

  return (
    <Space direction='horizontal' size='large' align='center'>
      <Space size='small'>
        <TextLabel>{intl.formatMessage(messages.statementDateLabel)}</TextLabel>
        <Text color='textSecondary' autoMargin={false}>
          {intl.formatMessage(messages.lastStatementCaption)}{' '}
          {lastStatementDate ? (
            <FormattedShortDate date={lastStatementDate} />
          ) : (
            intl.formatMessage(messages.noLastStatement)
          )}
        </Text>
      </Space>
      <Space.Item minWidth={200}>
        {/* Max date = today (BHV-035) */}
        <SingleDatePicker
          id='reconcile-statement-date'
          date={statementDateValue}
          onDateChange={onStatementDateChange}
          focused={isDatePickerFocused}
          onFocusChange={({ focused }) => onDatePickerFocusChange(focused)}
          isOutsideRange={isAfterToday}
          placeholder={intl.formatMessage(messages.statementDatePlaceholder)}
        />
      </Space.Item>
      <Space.Item minWidth={200}>
        <Field label={intl.formatMessage(messages.endingBalanceLabel)} required>
          <TextField
            id='reconcile-ending-balance'
            value={balanceValue}
            disabled={status === 'pending'}
            onChange={(event) => onBalanceChange(event.target.value)}
            onBlur={onBalanceBlur}
            onKeyDown={onBalanceKeyDown}
            inputProps={{
              'aria-label': intl.formatMessage(messages.endingBalanceAriaLabel),
            }}
          />
        </Field>
      </Space.Item>
      <Text bold autoMargin={false}>
        {currencyName}
      </Text>
    </Space>
  )
}

SessionStatementControls.displayName = 'SessionStatementControls'
